import { Avatar, Box, ButtonBase, Card, Stack, Tooltip, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PropTypes from "prop-types";
import { memo } from "react";
import {
  avatarColor,
  compactCount,
  getAuthorLabel,
  getAvatarUrl,
  getMediaInfo,
  initials,
  timeAgo,
} from "./communityUtils";
import { ZoomButton } from "./PostDialog";

// Rough pre-measurement height so unmeasured cards land near their final position.
export function estimateCardHeight(item) {
  const body = item?.body || item?.first_user_message || "";
  if (getMediaInfo(item).url) return 460;
  const lines = Math.min(10, Math.max(1, Math.ceil(body.length / 44)));
  return 140 + lines * 24;
}

const stop = (e) => e.stopPropagation();

function CommunityCard({ item, onOpenComments, onOpenPost, onOpenProfile, onZoomImage }) {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  const body = item?.body || item?.first_user_message || "";
  const { url: mediaUrl, posterUrl, isVideo } = getMediaInfo(item);
  const created = item?.createdAt || item?.created_at || "";
  const author = getAuthorLabel(item);
  const avatarUrl = getAvatarUrl(item);
  const hasMedia = Boolean(mediaUrl);
  const commentCount = Number(item?.commentCount) || 0;
  // Longer copy grows the card; short text-only posts stay compact so masonry packs tightly.
  const textClamp = hasMedia ? 3 : body.length > 220 ? 10 : 6;
  const muted = dark ? theme.palette.text.muted : "#64748b";
  const softMuted = dark ? alpha("#fff", 0.35) : "#94a3b8";
  // Dark: brighter surface + visible edge so cards read clearly on #0a0f1c.
  const hairline = dark ? alpha("#94a3b8", 0.38) : "rgba(15, 23, 42, 0.07)";
  const cardBg = dark ? "#243049" : theme.palette.background.card;
  const openComments = (e) => {
    e.stopPropagation();
    onOpenComments?.(item);
  };
  const openPost = () => onOpenPost?.(item);
  // Same as clicking the card; only "Answer" (no comments yet) jumps into the comment box.
  const openFromFooter = (e) => {
    if (commentCount) {
      e.stopPropagation();
      openPost();
    } else {
      openComments(e);
    }
  };

  return (
    <Card
      elevation={0}
      role="button"
      tabIndex={0}
      onClick={openPost}
      onKeyDown={(e) => {
        if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          openPost();
        }
      }}
      sx={{
        cursor: "pointer",
        "&:focus-visible": { outline: "2px solid #0061FE", outlineOffset: 2 },
        "@media (hover: hover)": {
          "& .card-zoom": { opacity: 0, transform: "scale(0.9)" },
          "&:hover .card-zoom, & .card-zoom:focus-visible": { opacity: 1, transform: "scale(1)" },
        },
        "&:hover .card-media-img": { transform: "scale(1.02)" },
        // Critical for masonry: auto height from content, never stretch to row peers.
        height: "auto",
        display: "block",
        width: "100%",
        overflow: "hidden",
        breakInside: "avoid",
        borderRadius: 4,
        border: `1px solid ${hairline}`,
        backgroundImage: "none",
        backgroundColor: cardBg,
        boxShadow: dark
          ? `0 0 0 1px ${alpha("#fff", 0.06)}, 0 12px 32px ${alpha("#000", 0.55)}`
          : "0 1px 2px rgba(15, 23, 42, 0.04)",
        transition: "box-shadow 180ms ease, border-color 180ms ease, transform 180ms ease",
        "&:hover": {
          borderColor: dark ? alpha("#4c8dff", 0.55) : "rgba(0, 97, 254, 0.18)",
          boxShadow: dark
            ? `0 0 0 1px ${alpha("#4c8dff", 0.35)}, 0 16px 40px ${alpha("#000", 0.65)}`
            : "0 12px 32px rgba(15, 23, 42, 0.08)",
          transform: dark ? "translateY(-1px)" : "none",
        },
      }}
    >
      {/* Author */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ px: 2.25, pt: 2.25, pb: 1.75 }}>
        <ButtonBase
          aria-label={`View ${author}'s profile`}
          onClick={(e) => {
            e.stopPropagation();
            onOpenProfile?.(item);
          }}
          sx={{
            borderRadius: "50%",
            flexShrink: 0,
            transition: "transform 160ms ease",
            "&:hover": { transform: "scale(1.06)" },
          }}
        >
        <Avatar
          src={avatarUrl || undefined}
          alt={author}
          sx={{
            width: 44,
            height: 44,
            flexShrink: 0,
            fontSize: 15,
            fontWeight: 700,
            letterSpacing: "0.02em",
            color: "#fff",
            bgcolor: avatarColor(author),
            boxShadow: dark
              ? `0 0 0 2px ${cardBg}, 0 0 0 3px ${alpha("#94a3b8", 0.4)}`
              : "0 0 0 2px #fff, 0 0 0 3px rgba(15, 23, 42, 0.06)",
          }}
        >
          {initials(author)}
        </Avatar>
        </ButtonBase>
        <Box minWidth={0} flex={1}>
          <Typography
            onClick={(e) => {
              e.stopPropagation();
              onOpenProfile?.(item);
            }}
            sx={{
              fontSize: 15,
              fontWeight: 700,
              lineHeight: 1.25,
              color: "ink.strong",
              letterSpacing: "-0.01em",
              width: "fit-content",
              maxWidth: "100%",
              "&:hover": { color: "#0061FE" },
            }}
            noWrap
          >
            {author}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.4, minWidth: 0 }}>
            <Tooltip title={created ? new Date(created).toLocaleString() : ""} placement="bottom-start">
              <Typography component="span" sx={{ fontSize: 12.5, color: muted, lineHeight: 1.2, whiteSpace: "nowrap" }}>
                {timeAgo(created)}
              </Typography>
            </Tooltip>
            {!hasMedia ? (
              <>
                <Box
                  component="span"
                  sx={{
                    width: 3,
                    height: 3,
                    borderRadius: "50%",
                    bgcolor: dark ? alpha("#fff", 0.28) : "#cbd5e1",
                    flexShrink: 0,
                  }}
                />
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.4,
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: dark ? "#6ea3ff" : "#0061FE",
                    lineHeight: 1.2,
                  }}
                >
                  <HelpOutlineRoundedIcon sx={{ fontSize: 14 }} />
                  Question
                </Box>
              </>
            ) : null}
          </Stack>
        </Box>
      </Stack>

      {/* Media */}
      {hasMedia ? (
        <Box sx={{ px: 1.5 }}>
          <Box
            sx={{
              position: "relative",
              borderRadius: 3,
              overflow: "hidden",
              bgcolor: isVideo ? "#000" : dark ? alpha("#fff", 0.06) : "#F1F5F9",
              border: dark ? `1px solid ${alpha("#fff", 0.08)}` : "none",
            }}
          >
            {isVideo ? (
              <Box
                component="video"
                controls
                onClick={stop}
                preload="metadata"
                src={mediaUrl}
                poster={posterUrl || undefined}
                sx={{
                  width: "100%",
                  display: "block",
                  maxHeight: { xs: 280, md: 360 },
                  objectFit: "contain",
                }}
              />
            ) : (
              <Box
                component="img"
                className="card-media-img"
                src={mediaUrl}
                alt=""
                loading="lazy"
                sx={{
                  transition: "transform 300ms ease",
                  width: "100%",
                  height: "auto",
                  display: "block",
                  objectFit: "cover",
                  // Keep native aspect so portrait/landscape create different card heights.
                  maxHeight: { xs: 260, md: 320 },
                }}
              />
            )}
            {!isVideo ? <ZoomButton className="card-zoom" onClick={() => onZoomImage?.(mediaUrl)} /> : null}
            {isVideo ? (
              <Box
                sx={{
                  position: "absolute",
                  top: 10,
                  left: 10,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.4,
                  px: 0.9,
                  py: 0.3,
                  borderRadius: 999,
                  fontSize: 11.5,
                  fontWeight: 700,
                  color: "#fff",
                  bgcolor: "rgba(2, 6, 23, 0.55)",
                  backdropFilter: "blur(6px)",
                  pointerEvents: "none",
                }}
              >
                <PlayArrowRoundedIcon sx={{ fontSize: 15 }} />
                Video
              </Box>
            ) : null}
          </Box>
        </Box>
      ) : null}

      {/* Body */}
      {body ? (
        <Typography
          sx={{
            px: 2.25,
            pt: hasMedia ? 1.75 : 0,
            fontSize: hasMedia ? 14.5 : 15.5,
            lineHeight: 1.55,
            fontWeight: hasMedia ? 500 : 400,
            color: "ink.main",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: textClamp,
            overflow: "hidden",
          }}
        >
          {body}
        </Typography>
      ) : null}

      {/* Footer */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{ px: 1.25, pt: 1.25, pb: 1.5 }}
      >
        <ButtonBase
          onClick={openFromFooter}
          aria-label={`${commentCount} comments`}
          sx={{
            gap: 0.6,
            px: 1,
            height: 32,
            borderRadius: 999,
            fontSize: 13,
            fontWeight: 600,
            color: "ink.soft",
            transition: "background 140ms ease, color 140ms ease",
            "&:hover": {
              color: dark ? "#6ea3ff" : "#0061FE",
              bgcolor: dark ? alpha("#4c8dff", 0.16) : "rgba(0, 97, 254, 0.08)",
            },
          }}
        >
          <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 17 }} />
          {compactCount(commentCount)}
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
            {commentCount === 1 ? "comment" : "comments"}
          </Box>
        </ButtonBase>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.6,
            px: 1,
            height: 32,
            fontSize: 13,
            fontWeight: 600,
            color: softMuted,
          }}
        >
          <VisibilityOutlinedIcon sx={{ fontSize: 17 }} />
          {compactCount(item?.viewCount)}
        </Box>
        <Box flex={1} />
        <ButtonBase
          onClick={openFromFooter}
          sx={{
            px: 1.5,
            height: 32,
            borderRadius: 999,
            fontSize: 13,
            fontWeight: 700,
            color: dark ? "#6ea3ff" : "#0061FE",
            "&:hover": {
              bgcolor: dark ? alpha("#4c8dff", 0.16) : "rgba(0, 97, 254, 0.08)",
            },
          }}
        >
          {commentCount ? "View discussion" : "Answer"}
        </ButtonBase>
      </Stack>
    </Card>
  );
}

CommunityCard.propTypes = {
  item: PropTypes.object.isRequired,
  onOpenComments: PropTypes.func,
  onOpenPost: PropTypes.func,
  onOpenProfile: PropTypes.func,
  onZoomImage: PropTypes.func,
};

export default memo(CommunityCard);
